import {
  Box,
  Button,
  Circle,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowUp } from "react-icons/fa";
import { BsRobot } from "react-icons/bs";
import { FiPlus } from "react-icons/fi";
import { LuSparkles } from "react-icons/lu";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { ArtifactProto, ChatClientType, chatClientTypeToJSON } from "build-ts/chat_session";
import { DarwinHeader } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { AvatarWithMemo, rotateAnimation } from "components/navbar/Navbar";
import logo from "../../../assets/logo_dark_small.png";
import { getRandomWelcomeMessage } from "utils/nameUtils";
import { useUser } from "context/UserContext";
import PromptButton from "./PromptButton";
import MessageInputAddMenu from "../containers/MessageInputAddMenu";
import MessageInput, { type MessageInputHandle } from "./MessageInput";
import ArtifactMentionMenu, { type ArtifactMentionMenuHandle } from "./ArtifactMentionMenu";
import Message from "./Message";
import { AppIcons } from "constants/icons";
import { PROMPT_BUTTONS } from "constants/prompts";
import { ArtifactOpenOpts, ChatMessage } from "../../interfaces";
import { ArtifactKind, ArtifactMap, buildArtifactSections } from "modules/artifacts/utils/artifactUtils";
import { labelMatchesQuery } from "modules/chat/utils/chatUtils";

export interface ChatPanelProps {
  isLoadingChat: boolean;
  chatId: string | undefined;
  chatMessages: ChatMessage[];
  sessionId?: string;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  selectedAnalyst?: Header | null;
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  scenarioJobs?: ScheduledJobProto[];
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  /** Full artifact registry for the current session — drives the @ mention menu. */
  artifactMap?: ArtifactMap;
  // ── Props provided by ChatContainer ──────────────────────────────────────
  isLoading?: boolean;
  streamingStatusLabel?: string;
  onSendMessage?: (inputMsg: string, newChat: boolean, analyst: Header | null | undefined) => Promise<void>;
  // True while an initialPrompt is waiting to auto-send
  // (e.g. while analysts are still loading after "Run Workflow").
  //
  isAutoSendPending?: boolean;
  currentSessionHeader?: DarwinHeader;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  isLoadingChat,
  chatId,
  chatMessages,
  sessionId,
  onArtifactClick,
  selectedAnalyst,
  isFromEva = false,
  analystsList = [],
  isLoadingAnalysts = false,
  onAnalystChange,
  isArtifactsPanelVisible = false,
  scenarioJobs,
  lookupByName,
  artifactMap,
  // Container-provided props
  isLoading = false,
  streamingStatusLabel = "Thinking ...",
  onSendMessage,
  isAutoSendPending = false,
  currentSessionHeader,
}) => {
  const navigate = useNavigate();

  const isInputDisabledDueToEva =
    isFromEva && !!currentSessionHeader && currentSessionHeader.type !== chatClientTypeToJSON(ChatClientType.EVA);

  // Block all send actions for new chats while the analysts list is still loading.
  // Without this guard the user would hit the "Please select an analyst first" error
  // even though an analyst is simply not yet resolved.
  const isBlockedByAnalystLoading = isLoadingAnalysts && !chatId && !sessionId;

  const { user, defaultTimeZone } = useUser();

  const onNewChat = useCallback(() => navigate("/chat/new"), [navigate]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [inputMessage, setInputMessage] = useState<string>("");
  /** null = mention menu closed; string = active query text after '@' */
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  /** Prompt text of the currently hovered prompt button — shown as a preview in the input. */
  const [hoveredPrompt, setHoveredPrompt] = useState<string | null>(null);

  const [welcomeMessage] = useState(() => {
    const clientObject = JSON.parse(user?.client_object_json || "{}");
    const ankoUserName = clientObject?.ankoUserName || "";
    return getRandomWelcomeMessage(ankoUserName);
  });

  // ── Scroll refs ───────────────────────────────────────────────────────────
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Becomes true when user manually scrolls away from the bottom.
  const userScrolledRef = useRef(false);
  // Tracks the previous message count so we can detect when a new message is added
  // vs. a deferred content-only reconciliation update to an existing message.
  const prevMessageCountRef = useRef(0);
  const highlightedTextareaRef = useRef<MessageInputHandle>(null);
  const mentionMenuRef = useRef<ArtifactMentionMenuHandle>(null);
  /** Ref attached to the outer input wrapper Box — used to anchor the mention menu. */
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  // Reset scroll refs synchronously in useLayoutEffect so they are zeroed before
  // the scroll check below runs in the same commit. useEffect fires after
  // useLayoutEffect, so placing ref mutations there would leave the scroll check
  // reading stale values on the first render of the new session.
  useLayoutEffect(() => {
    userScrolledRef.current = false;
    prevMessageCountRef.current = 0;
  }, [sessionId]);

  // Clear the draft whenever the user switches to a different chat (or starts a new one).
  // State setters trigger re-renders and belong in useEffect.
  useEffect(() => {
    setInputMessage("");
    setMentionQuery(null);
    setHoveredPrompt(null);
  }, [sessionId]);

  // ── Scroll helpers ────────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
      userScrolledRef.current = !isNearBottom;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current && !userScrolledRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Auto-scroll only when:
  //   • Streaming is active (isLoading=true) — follow live token/step updates, OR
  //   • A new message was added (count increased) — scroll to show it.
  //
  // Deliberately excluded: deferred React.startTransition reconciliation updates
  // (e.g. applyStreamChunk finalising executionSteps / content after isLoading=false).
  // Those fire after the user has a chance to scroll away and would otherwise push
  // them back to the bottom on every deferred render. The count check ensures we
  // still scroll when the user sends a new message or a new AI response is appended.
  useLayoutEffect(() => {
    const newCount = chatMessages.length;
    const messageAdded = newCount > prevMessageCountRef.current;
    prevMessageCountRef.current = newCount;

    if (!isLoadingChat && newCount > 0 && (isLoading || messageAdded)) {
      scrollToBottom();
    }
  }, [chatMessages, isLoadingChat, isLoading, scrollToBottom]);

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleInputChange = useCallback((value: string) => {
    setInputMessage(value);
    // Show the @ mention menu when the user types @ followed by non-whitespace
    // (or just @ alone). Detect the last unfinished @query before the cursor.
    const match = value.match(/@([^\s@]*)$/);
    if (match) {
      setMentionQuery(match[1]);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const handleSend = useCallback(() => {
    const msg = inputMessage;
    if (!msg.trim()) return;
    if (isBlockedByAnalystLoading) return;
    userScrolledRef.current = false;
    setInputMessage("");
    const isNew = !chatId && !sessionId;
    onSendMessage?.(msg, isNew, isNew ? selectedAnalyst : undefined);
  }, [inputMessage, chatId, sessionId, selectedAnalyst, isBlockedByAnalystLoading, onSendMessage]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // While the mention menu is open, arrow keys / Enter / Escape go to the menu.
      if (mentionQuery !== null && mentionMenuRef.current) {
        if (["ArrowUp", "ArrowDown", "Enter", "Escape"].includes(e.key)) {
          mentionMenuRef.current.handleKeyDown(e);
          return;
        }
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isLoading && !isBlockedByAnalystLoading) handleSend();
      }
    },
    [isLoading, isBlockedByAnalystLoading, handleSend, mentionQuery]
  );

  const hasMessages = useMemo(() => chatMessages.length > 0, [chatMessages.length]);

  // Filtered mention sections — only shown when the menu is open.
  const mentionSections = useMemo(() => {
    if (mentionQuery === null || !artifactMap) return [];
    const raw = buildArtifactSections(artifactMap);
    const q = mentionQuery.toLowerCase();
    if (!q) return raw;

    const matches = (label: string) => labelMatchesQuery(label, q).matched;

    return raw.map(s => ({ ...s, items: s.items.filter(i => matches(i.label)) })).filter(s => s.items.length > 0);
  }, [artifactMap, mentionQuery]);

  const handleMentionSelect = useCallback((tag: string) => {
    highlightedTextareaRef.current?.replaceAtMention(tag);
    setMentionQuery(null);
  }, []);

  const handleAnalysisSelect = useCallback((tag: string) => {
    highlightedTextareaRef.current?.insertTag(tag);
  }, []);

  // ── Analyst selector rendered inside the input box ────────────────────────

  const renderAnalystSelector = useCallback(
    (iconSize: number, fontSize: "xs" | "sm") => {
      if (isFromEva) return null;
      if (isLoadingAnalysts) return <Spinner size="xs" color="purple.500" flexShrink={0} />;
      return (
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            maxW="330px"
            bg="purple.50"
            fontSize={fontSize}
            fontWeight="600"
            borderRadius="lg"
            color="gray.700"
            _hover={{ bg: "purple.100" }}
            _active={{ bg: "purple.100" }}
            rightIcon={<ChevronDownIcon />}
            px={2}
            flexShrink={0}
            alignSelf="center"
          >
            <HStack spacing={1}>
              <Icon as={BsRobot} boxSize={iconSize} color="purple.600" />
              <Text isTruncated fontSize={fontSize} mb={0} fontWeight="600">
                {selectedAnalyst?.name || "Select analyst"}
              </Text>
            </HStack>
          </MenuButton>
          <Portal>
            <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
              {analystsList.length === 0 ? (
                <MenuItem isDisabled fontSize="sm" color="gray.500">
                  No analysts available
                </MenuItem>
              ) : (
                analystsList?.map(analyst => {
                  const isSelected = analyst.id === selectedAnalyst?.id;
                  return (
                    <MenuItem
                      key={analyst.id}
                      onClick={() => onAnalystChange?.({ target: { value: analyst.id } } as any)}
                      fontSize="sm"
                      bg={isSelected ? "purple.50" : undefined}
                      _hover={{ bg: "purple.100" }}
                      _focus={{ bg: "purple.100" }}
                      fontWeight={isSelected ? "700" : "500"}
                      color={isSelected ? "purple.700" : undefined}
                    >
                      <HStack spacing={2} w="100%" justify="space-between">
                        <Text mb={0}>{analyst.name}</Text>
                        {isSelected && <Icon as={BsRobot} boxSize={3.5} color="purple.600" flexShrink={0} />}
                      </HStack>
                    </MenuItem>
                  );
                })
              )}
            </MenuList>
          </Portal>
        </Menu>
      );
    },
    [isFromEva, isLoadingAnalysts, selectedAnalyst, analystsList, onAnalystChange]
  );

  // ── Input area ────────────────────────────────────────────────────────────

  const renderSendButton = useCallback(
    (size: "sm" | "md", iconSize: number, flexShrink?: number) => (
      <Tooltip label="Analyst is loading, please wait" isDisabled={!isBlockedByAnalystLoading} hasArrow placement="top">
        <Box display="inline-flex" flexShrink={flexShrink}>
          <IconButton
            icon={
              isLoading || isBlockedByAnalystLoading ? (
                <Spinner size="xs" color="white" />
              ) : (
                <Icon as={FaArrowUp} boxSize={iconSize} color="white" />
              )
            }
            onClick={handleSend}
            bg="purple.600"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva || isBlockedByAnalystLoading}
            aria-label="Send Chat Message"
            borderRadius="full"
            size={size}
            _hover={{ bg: "purple.700" }}
            _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
          />
        </Box>
      </Tooltip>
    ),
    [isLoading, isBlockedByAnalystLoading, handleSend, isLoadingChat, isInputDisabledDueToEva]
  );

  const renderInputArea = useMemo(
    () => (width: string) => (
      <Box
        ref={inputWrapperRef}
        w={width}
        border="1px solid"
        borderColor="gray.300"
        borderRadius="xl"
        bg="white"
        overflow="hidden"
        _focusWithin={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)" }}
        boxShadow="sm"
      >
        {!hasMessages ? (
          <>
            <MessageInput
              id="chat-input"
              ref={highlightedTextareaRef}
              value={inputMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder="How can I help?"
              previewText={hoveredPrompt ?? undefined}
              minHeight="90px"
              maxHeight="150px"
              isDisabled={isLoadingChat || isInputDisabledDueToEva}
              lookupByName={lookupByName}
              fontSize="md"
              px={3}
              pt={3}
              pb={1}
              fontWeight="500"
            />
            <HStack justify="space-between" spacing={2} m={2}>
              {/* Left: + menu */}
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<Icon as={FiPlus} boxSize={5} color="purple.600" />}
                  size="sm"
                  variant="ghost"
                  borderRadius="full"
                  bg="purple.50"
                  _hover={{ bg: "purple.100" }}
                  aria-label="Add attachment"
                  flexShrink={0}
                />
                <MessageInputAddMenu
                  sessionHeader={currentSessionHeader}
                  analyst={selectedAnalyst ?? undefined}
                  chatId={chatId ?? ""}
                  scenarioJobs={scenarioJobs}
                  onSelectAnalysis={handleAnalysisSelect}
                />
              </Menu>
              {/* Right: analyst selector + send */}
              <HStack spacing={2}>
                {renderAnalystSelector(4, "xs")}
                {renderSendButton("md", 5)}
              </HStack>
            </HStack>
          </>
        ) : (
          // Not empty: stacked layout — textarea on top, toolbar row below
          <>
            <MessageInput
              ref={highlightedTextareaRef}
              value={inputMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder="Reply..."
              minHeight="44px"
              maxHeight="150px"
              isDisabled={isLoadingChat || isInputDisabledDueToEva}
              lookupByName={lookupByName}
              fontSize="md"
              px={3}
              pt={3}
              pb={1}
            />
            <HStack justify="space-between" spacing={2} m={2}>
              {/* Left: + menu */}
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<Icon as={FiPlus} boxSize={5} color="purple.600" />}
                  size="sm"
                  variant="ghost"
                  borderRadius="full"
                  bg="purple.50"
                  _hover={{ bg: "purple.100" }}
                  aria-label="Add attachment"
                  flexShrink={0}
                />
                <MessageInputAddMenu
                  sessionHeader={currentSessionHeader}
                  analyst={selectedAnalyst ?? undefined}
                  chatId={chatId ?? ""}
                  scenarioJobs={scenarioJobs}
                  onSelectAnalysis={handleAnalysisSelect}
                />
              </Menu>
              {/* Right: analyst selector (switches to new chat on change) + send */}
              <HStack spacing={2}>
                {renderAnalystSelector(4, "xs")}
                {renderSendButton("sm", 4, 0)}
              </HStack>
            </HStack>
          </>
        )}
      </Box>
    ),
    [
      hasMessages,
      inputMessage,
      hoveredPrompt,
      handleInputChange,
      handleKeyPress,
      isLoadingChat,
      isInputDisabledDueToEva,
      currentSessionHeader,
      selectedAnalyst,
      chatId,
      scenarioJobs,
      renderAnalystSelector,
      renderSendButton,
    ]
  );

  // ── Loading state ─────────────────────────────────────────────────────────

  if (isLoadingChat) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Loading...
        </Text>
      </Box>
    );
  }

  // This loader will be shown only when "Run Workflow" button is clicked.
  if (isAutoSendPending) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
        <Spinner size="md" color="purple.700" thickness="2px" mr={2} />
        <Text fontSize="md" color="gray.500" mb={0}>
          Preparing your chat...
        </Text>
      </Box>
    );
  }

  // ── Empty state — centered layout ─────────────────────────────────────────

  if (!hasMessages) {
    return (
      <Box position="relative" w="100%" h="100%" display="flex" flexDirection="column">
        {mentionQuery !== null && (
          <ArtifactMentionMenu
            ref={mentionMenuRef}
            sections={mentionSections}
            onSelect={handleMentionSelect}
            onClose={() => setMentionQuery(null)}
            anchorRef={inputWrapperRef}
          />
        )}
        <VStack flex={1} w="100%" h="100%" justify="center" align="center" spacing={9} mt={-3}>
          <HStack spacing={5}>
            <Circle bg="purple.50" p={2}>
              <AvatarWithMemo src={logo} animation={`${rotateAnimation} 3s linear infinite`} name="Anko" size="md" />
            </Circle>
            <Text color="gray.700" fontSize="3xl" fontWeight="400" m={0}>
              {welcomeMessage}
            </Text>
          </HStack>
          {renderInputArea("700px")}
          {/* Prompt buttons - FRIDA chat only */}
          {!isFromEva && (
            <VStack spacing={3} align="flex-start" maxW="700px" w="100%">
              <HStack
                display={{ base: "none", md: "flex" }}
                flexShrink={0}
                color="purple.600"
                fontSize="sm"
                fontWeight="medium"
              >
                <Icon as={LuSparkles} boxSize={4} />
                <Text m={0}>Try these</Text>
              </HStack>
              <SimpleGrid columns={2} spacing={2} w="100%">
                {PROMPT_BUTTONS.map(btn => (
                  <Tooltip
                    key={btn.title}
                    label="Analyst is loading, please wait"
                    isDisabled={!isBlockedByAnalystLoading}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                  >
                    <PromptButton
                      icon={<Icon as={AppIcons[btn.iconKey]} boxSize={5} />}
                      title={btn.title}
                      description={btn.helpText}
                      isDisabled={isBlockedByAnalystLoading}
                      onMouseEnter={() => setHoveredPrompt(btn.prompt)}
                      onMouseLeave={() => setHoveredPrompt(null)}
                      onClick={() => {
                        onSendMessage?.(btn.prompt, !(chatId || sessionId), selectedAnalyst);
                      }}
                      w="100%"
                    />
                  </Tooltip>
                ))}
              </SimpleGrid>
            </VStack>
          )}
        </VStack>
      </Box>
    );
  }

  // ── With messages — bottom input layout ──────────────────────────────────

  return (
    <VStack w="100%" h="100%" m={0} spacing={0} mt={1} position="relative">
      {mentionQuery !== null && (
        <ArtifactMentionMenu
          ref={mentionMenuRef}
          sections={mentionSections}
          onSelect={handleMentionSelect}
          onClose={() => setMentionQuery(null)}
          anchorRef={inputWrapperRef}
        />
      )}
      <Box
        ref={messagesContainerRef}
        flex="1"
        minH="0"
        w="100%"
        overflow="auto"
        onScroll={handleScroll}
        p={4}
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-gray-200)",
            borderRadius: "full",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "var(--chakra-colors-gray-400)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "var(--chakra-colors-gray-400) transparent",
        }}
      >
        <VStack w={isArtifactsPanelVisible ? "100%" : "80%"} mx="auto" align="stretch" spacing={0}>
          <Box flex="1" minH="0" />
          {chatMessages?.map((msg, index) => (
            <Message
              key={msg.id ?? `${msg.author}-${index}`}
              author={msg.author as "user" | "ai"}
              content={msg.content}
              responseMessage={msg.responseMessage}
              artifacts={msg.artifacts}
              onArtifactClick={onArtifactClick}
              isLoading={isLoading && index === chatMessages.length - 1 && msg.author === "ai"}
              isLastMessage={index === chatMessages.length - 1}
              streamingStatusLabel={streamingStatusLabel}
              executionSteps={msg.executionSteps}
              streamingReasoningGroups={msg.streamingReasoningGroups}
              timelineOrder={msg.timelineOrder}
              prevMessageEndTimestampMs={msg.prevMessageEndTimestampMs}
              lsTraceUrl={isFromEva ? msg.ls_trace_url : undefined}
              // Perf optimization: only the latest AI message needs full chat history context
              // during active streaming; passing non-null chatMessages to older messages causes
              // avoidable re-renders on every token/chunk update.
              chatMessages={msg.author === "ai" && index === chatMessages.length - 1 ? chatMessages : undefined}
              isArtifactsPanelVisible={isArtifactsPanelVisible}
              lookupByName={lookupByName}
              scenarioJobs={scenarioJobs}
              userTimestampMs={msg.userTimestampMs}
              executionStatus={msg.executionStatus}
              defaultTimeZone={defaultTimeZone}
              onNewChat={onNewChat}
            />
          ))}
        </VStack>
      </Box>
      <Box w="100%" p={4} bg="bg.surface" display="flex" flexDirection="column" alignItems="center" mb={1}>
        {renderInputArea(isArtifactsPanelVisible ? "95%" : "80%")}
        <Text fontSize="xs" color="fg.muted" mt={2} mb={0}>
          Anko is AI and may make mistakes. Please double check responses.
        </Text>
      </Box>
    </VStack>
  );
};

export default ChatPanel;
